import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import API from './API';
import Utils from './Utils';
import SubscriptionModal from '../components/SubscriptionModal';

// Contexte de la formule courante (GET /admin/me/plan). Monté UNE seule fois autour du Switch (dans
// HomePage) pour ne pas re-fetcher à chaque navigation admin — sinon `locked` repasse à false ~1s et
// le gating des ressources saute. Expose openSubscription() qui ouvre la modale d'abonnement, montée
// ici (déclenchée par le menu profil ET par les ressources bloquées de la formule gratuite).
const PlanContext = createContext({
  plan: null,
  interval: null,
  cancelAtPeriodEnd: false,
  subscriptionEndAt: null,
  // Compte payant dont la période payée est dépassée : plan vaut 'FREE' (bridé comme un gratuit), expired
  // le distingue pour l'affichage. paymentFailed : impayé en cours (bandeau + toast).
  expired: false,
  paymentFailed: false,
  country: null,
  vatNumber: null,
  onlinePaymentDisabled: false,
  // Compte Stripe Connect rattaché : sans lui, l'option « paiement en ligne » est verrouillée.
  stripeConnected: false,
  resourceLimit: null,
  bookingsUsedThisMonth: 0,
  bookingQuota: null,
  refresh: () => {},
  openSubscription: () => {},
});

export const PlanProvider = ({intl, children}) => {
  const history = useHistory();
  const [plan, setPlan] = useState(null);
  const planRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  // Raison d'ouverture : 'RESOURCE_LIMIT' quand on arrive depuis un plafond de ressource (bouton « Nouveau »
  // verrouillé). La modale affiche alors un bandeau explicatif. null quand ouverte depuis le menu profil.
  const [limitReason, setLimitReason] = useState(null);

  const fetchPlan = useCallback(async () => {
    if (!Utils.isAccessToken(history, true)) return;
    const response = await API.getPlan(history);
    if (!response || !response.data || response.data.success !== true) return;
    planRef.current = response.data.data || {};
    setPlan(planRef.current);
  }, [history]);

  useEffect(() => {
    fetchPlan();
    // Le provider est monté une seule fois, au démarrage de l'app — souvent AVANT la connexion (donc
    // sans token, fetchPlan sort tout de suite). Après un login en navigation SPA (history.push), il ne
    // se re-monte pas : sans ceci le plan resterait null (formule "Mensuel" + dialog vide, ressources
    // non verrouillées). On refetch donc à chaque navigation TANT QUE le plan n'est pas chargé ; une
    // fois chargé (planRef non null), on n'y touche plus — pas de refetch par navigation.
    const unlisten = history.listen(() => {
      if (planRef.current === null) fetchPlan();
    });
    return unlisten;
  }, [fetchPlan, history]);

  // Il n'existe pas de page d'abonnement : les liens externes (bouton « Passer au payant » de l'email de
  // dépassement de quota) pointent sur /#/admin/dashboard?subscription=1 et ouvrent cette modale. On
  // n'ouvre rien si l'utilisateur n'est pas connecté (il est renvoyé sur la connexion) et on retire le
  // paramètre de l'URL pour qu'un rafraîchissement ne rouvre pas la modale.
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    if (params.get('subscription') !== '1') return;
    params.delete('subscription');
    const search = params.toString();
    history.replace({pathname: history.location.pathname, search: search ? `?${search}` : ''});
    if (Utils.isAccessToken(history, true)) {
      fetchPlan();
      setLimitReason(null); // dépassement de quota de réservations : contexte différent, pas le bandeau ressource
      setShowModal(true);
    }
  }, [fetchPlan, history]);

  const value = useMemo(() => ({
    ...(plan || {}),
    refresh: fetchPlan,
    openSubscription: (reason) => {
      fetchPlan(); // s'assure que la modale affiche la formule à jour, même juste après le login
      // On ne retient que les raisons explicites (chaîne) : certains appels passent l'événement onClick
      // en 1er argument (ex. menu profil) — il ne doit pas déclencher le bandeau « limite atteinte ».
      setLimitReason(typeof reason === 'string' ? reason : null);
      setShowModal(true);
    },
  }), [plan, fetchPlan]);

  return (
    <PlanContext.Provider value={value}>
      {children}
      <SubscriptionModal intl={intl} plan={value} limitReason={limitReason} show={showModal} onClose={() => setShowModal(false)}/>
    </PlanContext.Provider>
  );
};

export const usePlan = () => useContext(PlanContext);
